import { Combobox, Divider, extractStyleProps, Input, InputBase, Pill, PillsInput, ScrollArea, useCombobox } from "@mantine/core";
import { useDidUpdate, useId, useMergedRef, useUncontrolled } from "@mantine/hooks";
import classNames from "classnames";
import * as React from "react";

import { type ElementProps, rem, type SizeValue, type StyleProps } from "../primitives/style-props";

import classes from "./selects.module.css";

/**
 * The application-owned option shape. Metadata belongs to each consumer's concrete item type.
 */
export interface SelectItem {
  value: string;
  label?: string;
  selected?: boolean;
  disabled?: boolean;
  group?: string;
}

/** Props supplied to a custom option renderer, including the application's item metadata. */
export type SelectItemComponentProps<Item extends SelectItem = SelectItem> = React.ComponentPropsWithoutRef<"div"> & Item;

/**
 * Props supplied to a custom selected-value renderer. `classNames` is retained only because two
 * existing renderers deliberately discard Mantine 6's value-token style map; styling itself stays
 * outside the public contract, and the Mantine 7 adapter no longer supplies one.
 */
export type MultiSelectValueProps<Item extends SelectItem = SelectItem> = React.ComponentPropsWithoutRef<"div"> &
  Item & {
    label: string;
    onRemove: () => void;
    disabled: boolean;
    readOnly: boolean;
    classNames?: unknown;
  };

/** Renderer call signature accepted by both function and forward-ref components. */
type SelectRenderer<Props> = (props: Props) => React.ReactNode;

interface FieldBaseProps {
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  withAsterisk?: boolean;
  labelProps?: Record<string, unknown>;
  descriptionProps?: Record<string, unknown>;
  errorProps?: Record<string, unknown>;
  wrapperProps?: Record<string, unknown>;
  inputContainer?: (children: React.ReactNode) => React.ReactNode;
  inputWrapperOrder?: Array<"label" | "input" | "description" | "error">;
}

interface SelectSharedProps<Item extends SelectItem, Value>
  extends Omit<ElementProps<"input">, "defaultValue" | "onChange" | "size" | "value">, StyleProps, FieldBaseProps {
  /** Select data; strings use the same value and label. */
  data: ReadonlyArray<string | Item>;
  value?: Value;
  defaultValue?: Value;
  onChange?(value: Value): void;
  filter?: (query: string, item: Item) => boolean;
  /** Custom option renderer. Its item metadata is inferred from `data`. */
  itemComponent?: SelectRenderer<SelectItemComponentProps<Item>>;
  icon?: React.ReactNode;
  iconWidth?: React.CSSProperties["width"];
  rightSection?: React.ReactNode;
  rightSectionWidth?: React.CSSProperties["width"];
  rightSectionProps?: Record<string, unknown>;
  radius?: SizeValue;
  variant?: "default" | "filled" | "unstyled";
  /** Non-numeric: Mantine resolves Select size by theme key only. */
  size?: Exclude<SizeValue, number>;
  shadow?: string;
  initiallyOpened?: boolean;
  onDropdownOpen?: () => void;
  onDropdownClose?: () => void;
  /** Omitted renders inline, as Mantine 6 did; the adapter supplies that default over Mantine 7's portal. */
  withinPortal?: boolean;
  limit?: number;
  nothingFound?: React.ReactNode;
  zIndex?: React.CSSProperties["zIndex"];
  dropdownPosition?: "bottom" | "top" | "flip";
  switchDirectionOnFlip?: boolean;
  positionDependencies?: unknown[];
  /** A named class for the option slot; unlike `styles`, it is stable application API. */
  itemClassName?: string;
  /** A named class for the input slot; unlike `styles`, it is stable application API. */
  inputClassName?: string;
  /** A named class for the input wrapper root; unlike `styles`, it is stable application API. */
  rootClassName?: string;
  dropdownComponent?: React.ElementType;
  clearButtonProps?: React.ComponentPropsWithoutRef<"button">;
}

export interface SelectProps<Item extends SelectItem = SelectItem> extends SelectSharedProps<Item, string | null> {
  maxDropdownHeight?: number;
  searchable?: boolean;
  clearable?: boolean;
  onSearchChange?: (query: string) => void;
  /** User query only; selection, blur, clear, and selected-label synchronization emit an empty query. */
  onSearchQueryChange?: (query: string) => void;
  searchValue?: string;
  hoverOnSearchChange?: boolean;
  creatable?: boolean;
  getCreateLabel?: (query: string) => React.ReactNode;
  shouldCreate?: (query: string, data: Item[]) => boolean;
  onCreate?: (query: string) => SelectItem | string | null | undefined;
  selectOnBlur?: boolean;
  allowDeselect?: boolean;
  filterDataOnExactSearchMatch?: boolean;
}

export interface MultiSelectProps<Item extends SelectItem = SelectItem> extends Omit<SelectSharedProps<Item, string[]>, "filter"> {
  /** Custom selected-value renderer. Its item metadata is inferred from `data`. */
  valueComponent?: SelectRenderer<MultiSelectValueProps<Item>>;
  maxDropdownHeight?: number | string;
  searchable?: boolean;
  filter?: (query: string, selected: boolean, item: Item) => boolean;
  clearSearchOnChange?: boolean;
  clearable?: boolean;
  disableSelectedItemFiltering?: boolean;
  clearSearchOnBlur?: boolean;
  onSearchChange?: (query: string) => void;
  searchValue?: string;
  hoverOnSearchChange?: boolean;
  creatable?: boolean;
  getCreateLabel?: (query: string) => React.ReactNode;
  shouldCreate?: (query: string, data: Item[]) => boolean;
  onCreate?: (query: string) => SelectItem | string | null | undefined;
  maxSelectedValues?: number;
  selectOnBlur?: boolean;
}

/*
 * Mantine 7 replaced Mantine 6's Select, MultiSelect and Autocomplete with components over its Combobox
 * primitives, and dropped the contract this file owns: `itemComponent`, `valueComponent`, `creatable`,
 * `nothingFound`, the Mantine 6 filter signatures, `allowDeselect` defaulting to `clearable`, item metadata
 * (its parser drops metadata from label-less items and treats any `group` key as a nested group) and
 * duplicate values (it throws). The adapter below therefore keeps Mantine 6's data, filtering, creation
 * and selection rules itself and renders them through the public Combobox primitives (SC-2703). Every
 * rule cites the Mantine 6 behavior it preserves.
 */

/** One dropdown row: a caller item, or Mantine 6's trailing row that creates the typed query. */
type OptionEntry = { kind: "item"; item: SelectItem } | { kind: "create"; query: string };

type OptionsDirection = "column" | "column-reverse";

/** Mantine 6 flipped only for `dropdownPosition="flip"` and never shifted along the input. */
const FLIP_MIDDLEWARES = { flip: true, shift: false };
const FIXED_MIDDLEWARES = { flip: false, shift: false };
const REVERSED_OPTIONS: React.CSSProperties = { display: "flex", flexDirection: "column-reverse" };

const preventDefault = (event: React.SyntheticEvent) => event.preventDefault();

/** Mantine 6 lifted string data to `{ value, label }` and kept each object item, metadata included, as given. */
const toItems = (data: ReadonlyArray<string | SelectItem>): SelectItem[] =>
  data.map(item => (typeof item === "string" ? { value: item, label: item } : item));

/** Mantine 6's `groupOptions`: grouped items first, in first-seen group order, then the ungrouped ones. */
const groupItems = (items: SelectItem[]): SelectItem[] => {
  const grouped: Record<string, SelectItem[]> = {};
  const ungrouped: SelectItem[] = [];
  items.forEach(item => {
    if (item.group) {
      (grouped[item.group] ??= []).push(item);
    } else {
      ungrouped.push(item);
    }
  });
  return [...Object.values(grouped).flat(), ...ungrouped];
};

const includesQuery = (text: string | undefined, query: string): boolean =>
  (text ?? "").toLowerCase().trim().includes(query.toLowerCase().trim());

/** Mantine 6's default filters: Select and MultiSelect match the label, Autocomplete the value. */
const defaultSelectFilter = (query: string, item: SelectItem): boolean => includesQuery(item.label, query);
const defaultMultiSelectFilter = (query: string, selected: boolean, item: SelectItem): boolean =>
  !selected && includesQuery(item.label, query);
const defaultAutocompleteFilter = (query: string, item: SelectItem): boolean => includesQuery(item.value, query);

/** Mantine 6's default creation guards: Select compares labels of the visible rows, MultiSelect values of all rows. */
const defaultSelectShouldCreate = (query: string, items: SelectItem[]): boolean =>
  !!query && !items.some(item => (item.label ?? "").toLowerCase() === query.toLowerCase());
const defaultMultiSelectShouldCreate = (query: string, items: SelectItem[]): boolean =>
  !!query && !items.some(item => item.value.toLowerCase() === query.toLowerCase());

const takeMatches = (items: SelectItem[], limit: number, matches: (item: SelectItem) => boolean): SelectItem[] => {
  const result: SelectItem[] = [];
  for (const item of items) {
    if (matches(item)) {
      result.push(item);
    }
    if (result.length >= limit) {
      break;
    }
  }
  return result;
};

interface SelectFilterInput {
  items: SelectItem[];
  searchable: boolean;
  limit: number;
  search: string;
  filter: (query: string, item: SelectItem) => boolean;
  value: string | null;
  filterDataOnExactSearchMatch: boolean;
}

/**
 * Mantine 6 Select's `filterData`: nothing is filtered unless searchable, and a query equal to the selected
 * label shows the unfiltered list (a `limit`-sized window starting at the selected row).
 */
const filterSelectItems = ({ items, searchable, limit, search, filter, value, filterDataOnExactSearchMatch }: SelectFilterInput) => {
  if (!searchable) {
    return items;
  }

  const selected = value !== null ? items.find(item => item.value === value) : undefined;
  if (selected && !filterDataOnExactSearchMatch && selected.label === search) {
    if (!limit || limit >= items.length) {
      return items;
    }
    const firstIndex = items.indexOf(selected);
    const overflow = firstIndex + limit - items.length;
    return overflow > 0 ? items.slice(firstIndex - overflow) : items.slice(firstIndex, firstIndex + limit);
  }

  return takeMatches(items, limit, item => filter(search, item));
};

interface MultiSelectFilterInput {
  items: SelectItem[];
  searchable: boolean;
  limit: number;
  search: string;
  filter: (query: string, selected: boolean, item: SelectItem) => boolean;
  value: string[];
  disableSelectedItemFiltering: boolean;
}

/** Mantine 6 MultiSelect's `filterData`: selected rows leave the list unless selected-item filtering is disabled. */
const filterMultiSelectItems = ({
  items,
  searchable,
  limit,
  search,
  filter,
  value,
  disableSelectedItemFiltering,
}: MultiSelectFilterInput) => {
  const isSelected = (item: SelectItem) => value.some(selectedValue => selectedValue === item.value && !item.disabled);
  if (!searchable) {
    return value.length === 0 || disableSelectedItemFiltering ? items : items.filter(item => !isSelected(item));
  }
  return takeMatches(items, limit, item => filter(search, !disableSelectedItemFiltering && isSelected(item), item));
};

/** Mantine 6 MultiSelect kept only values present in `data` (none while `data` is empty). */
const valuesInData = (value: string[] | undefined, data: ReadonlyArray<string | SelectItem>): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const dataValues = data.map(item => (typeof item === "string" ? item : item.value));
  return value.filter(selectedValue => dataValues.includes(selectedValue));
};

/** The rows in Mantine 6's order: the filtered items, then the create row when there is a query to create. */
const toEntries = (items: SelectItem[], createQuery: string | null): OptionEntry[] => {
  const entries = items.map((item): OptionEntry => ({ kind: "item", item }));
  return createQuery === null ? entries : [...entries, { kind: "create", query: createQuery }];
};

interface RightSectionInput {
  readOnly?: boolean;
  disabled?: boolean;
  shouldClear: boolean;
  onClear: () => void;
  clearButtonProps?: React.ComponentPropsWithoutRef<"button">;
  error?: React.ReactNode;
  size?: string;
}

/** Mantine 6's `SelectRightSection`: a clear button while there is a value to clear, else the chevron; none read-only. */
const renderDefaultRightSection = ({ readOnly, disabled, shouldClear, onClear, clearButtonProps, error, size }: RightSectionInput) => {
  if (readOnly || (disabled && shouldClear)) {
    return null;
  }
  return shouldClear ? (
    <Input.ClearButton {...clearButtonProps} onClick={onClear} onMouseDown={preventDefault} />
  ) : (
    <Combobox.Chevron error={error} size={size} />
  );
};

interface DropdownCallbacks {
  onDropdownOpen?: () => void;
  onDropdownClose?: () => void;
}

/** The event sources Mantine 7's combobox store reports an open with. */
type OpenSource = "keyboard" | "mouse" | "unknown";

interface SelectComboboxInput extends DropdownCallbacks {
  initiallyOpened: boolean;
  /** The rows the dropdown renders, in order. */
  entries: OptionEntry[];
  /** The typed query: Select's and MultiSelect's search, Autocomplete's value. */
  query: string;
  hoverOnSearchChange: boolean;
  /**
   * Select's keyboard open while it holds a value: Mantine 6 hovered that value's row (`findIndex`), so nothing once
   * the query had filtered the row out; only without a value did it hover the first row.
   */
  valueRowOnly?: boolean;
  /** MultiSelect clamped Mantine 6's hovered index to its last row (`Math.min`), so picking the last row hovers the new last. */
  clampSelection?: boolean;
}

/** A row's React identity, so the key changes exactly when the rendered rows or their order change. */
const toRowKey = (entry: OptionEntry) => (entry.kind === "item" ? entry.item.value : null);

/**
 * Mantine 6's Select rendered nothing for a closed dropdown; Mantine 7's Combobox keeps closed options mounted so
 * its keyboard store can find them. The adapter unmounts them (`keepMounted={false}` below), so once an opened list
 * mounts it applies the hover Mantine 6 gave it (SC-2703): a keyboard open hovers the current value's row, else the
 * first row (Select's exception is `valueRowOnly`); a pointer open (only Select reports one) hovers the current
 * value's row; any other open (focus, typing, Enter) hovers nothing, so Enter cannot re-pick the current value, and
 * only scrolls that row into view.
 *
 * Mantine 6 kept one hovered index that both drew the hover and chose the row Enter picked; the store's selected
 * option plays that part, and the rules below move it where Mantine 6 moved that index.
 */
const useSelectCombobox = ({
  initiallyOpened,
  entries,
  query,
  hoverOnSearchChange,
  valueRowOnly = false,
  clampSelection = false,
  onDropdownOpen,
  onDropdownClose,
}: SelectComboboxInput) => {
  const openSource = React.useRef<OpenSource>("unknown");
  const optionsRef = React.useRef<HTMLDivElement>(null);
  const store = useCombobox({
    defaultOpened: initiallyOpened,
    // Mantine 6's arrow keys stopped at the first and last rows; Mantine 7 wraps unless told not to.
    loop: false,
    onDropdownOpen: source => {
      openSource.current = source;
      onDropdownOpen?.();
    },
    onDropdownClose: () => {
      onDropdownClose?.();
      store.resetSelectedOption();
    },
  });
  // Mantine 6's ArrowUp with no row hovered left none hovered. Mantine 7's previous index from none is still none,
  // which its `selectOption` turns into the last row, so that press is kept a no-op.
  const combobox = {
    ...store,
    selectPreviousOption: () => (store.getSelectedOptionIndex() === -1 ? null : store.selectPreviousOption()),
  };

  // The store indexes these rows in DOM order; `selectOption` and `clickSelectedOption` both read this list.
  const renderedOptions = () => Array.from(optionsRef.current?.querySelectorAll<HTMLElement>("[data-combobox-option]") ?? []);

  /** Mantine 6 hovered the row under the pointer (`onMouseEnter`), so Enter picked it rather than submitting the form. */
  const hoverOption = (option: HTMLElement) => {
    const index = renderedOptions().indexOf(option);
    if (index === -1) {
      return;
    }
    // A disabled row took Mantine 6's index without drawing a hover, so the previous row's hover is cleared first.
    combobox.resetSelectedOption();
    combobox.selectOption(index);
  };

  useDidUpdate(() => {
    if (!combobox.dropdownOpened) {
      return undefined;
    }
    if (openSource.current === "keyboard") {
      if (valueRowOnly && !optionsRef.current?.querySelector("[data-combobox-active]")) {
        combobox.resetSelectedOption();
      } else {
        combobox.selectActiveOption();
      }
      return undefined;
    }
    if (openSource.current === "mouse") {
      combobox.updateSelectedOptionIndex("active", { scrollIntoView: true });
      return undefined;
    }
    // `updateSelectedOptionIndex` would also arm the row for Enter; this scrolls it only, on the store's timing.
    const timeout = window.setTimeout(() => {
      optionsRef.current?.querySelector("[data-combobox-active]")?.scrollIntoView({ block: "nearest" });
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [combobox.dropdownOpened]);

  // Mantine 6 hovered the first match on each query change with `hoverOnSearchChange`, and nothing otherwise.
  useDidUpdate(() => {
    if (hoverOnSearchChange && query) {
      combobox.selectFirstOption();
    } else {
      combobox.resetSelectedOption();
    }
  }, [query, hoverOnSearchChange]);

  // Mantine 7 draws the selected option as an attribute on its keyed row, which travels with that row when the rows
  // change, while Enter clicks the row at the stored index. Mantine 6 drew the hover and picked from one index, so
  // when the rows change under a selected index the hover is redrawn on the row now at that index (none past the end).
  // This runs after the query rule above, so a typed query still clears the selection.
  const rowsKey = JSON.stringify(entries.map(toRowKey));
  useDidUpdate(() => {
    const selectedIndex = combobox.getSelectedOptionIndex();
    if (selectedIndex === -1) {
      return;
    }
    const rowCount = renderedOptions().length;
    const index = clampSelection ? Math.min(selectedIndex, rowCount - 1) : selectedIndex;
    combobox.resetSelectedOption();
    if (index >= 0 && index < rowCount) {
      combobox.selectOption(index);
    }
  }, [rowsKey]);

  return { combobox, optionsRef, hoverOption };
};

/**
 * Mantine 6 spread `rightSectionProps` onto the right section as a loose record; Mantine 7 types the same spread as
 * the section div's props. The owned prop keeps its record type, so this narrows it for the forward only; the
 * `selects-dom.test.ts` pin proves a forwarded key lands on that section `div`.
 */
const toSectionProps = (props: Record<string, unknown> | undefined) => props as React.ComponentPropsWithoutRef<"div"> | undefined;

/**
 * Mantine 6's Input hid its right section while the input inside was disabled (6.0.19 Input/Input.styles.js:96-100,
 * `&:has(input:disabled) .mantine-Input-rightSection { display: none }`); Mantine 7 never hides it, so each select
 * wrapper restates that over the caller's section props, as the `<input>`-backed controls' mantine6RightSection does (SC-2703).
 */
const toRightSectionProps = (props: Record<string, unknown> | undefined, disabled: boolean | undefined) => {
  const sectionProps = toSectionProps(props);
  return disabled ? { ...sectionProps, style: { ...sectionProps?.style, display: "none" } } : sectionProps;
};

interface OptionListProps {
  entries: OptionEntry[];
  createLabel: React.ReactNode;
  isSelected: (value: string) => boolean;
  itemComponent?: SelectRenderer<SelectItemComponentProps>;
  itemClassName?: string;
  /** Arms the row under the pointer, as Mantine 6's `onItemHover` did. */
  onOptionHover: (option: HTMLElement) => void;
}

/**
 * Mantine 6's `SelectItems`: grouped rows under their group label, a separator, then the ungrouped rows and the
 * create row. A custom renderer receives the complete item, metadata included, plus `selected` and the padded row
 * class on its root, as it did; the Combobox option around it owns the option role, keyboard state and clicks.
 */
const OptionList = ({ entries, createLabel, isSelected, itemComponent: ItemComponent, itemClassName, onOptionHover }: OptionListProps) => {
  const optionClassName = classNames(classes.option, itemClassName);
  const renderedOptionClassName = classNames(classes.option, classes.renderedOption, itemClassName);
  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => onOptionHover(event.currentTarget);

  // The option value is the row index, not the item value: Mantine 6 resolved a pick by row, so two items sharing
  // a value still submit the one that was picked.
  const renderEntry = (entry: OptionEntry, index: number): React.ReactElement => {
    if (entry.kind === "create") {
      return (
        <Combobox.Option className={optionClassName} key="create" onMouseEnter={handleMouseEnter} value={String(index)}>
          {createLabel}
        </Combobox.Option>
      );
    }

    const { item } = entry;
    const selected = isSelected(item.value);
    return (
      <Combobox.Option
        active={selected}
        aria-selected={selected}
        className={ItemComponent ? renderedOptionClassName : optionClassName}
        data-checked={selected || undefined}
        disabled={item.disabled}
        key={item.value}
        onMouseEnter={handleMouseEnter}
        value={String(index)}
      >
        {ItemComponent ? <ItemComponent className={classes.renderedRow} selected={selected} {...item} /> : item.label || item.value}
      </Combobox.Option>
    );
  };

  const groups: Array<{ label: string; options: React.ReactElement[] }> = [];
  const ungrouped: React.ReactElement[] = [];
  entries.forEach((entry, index) => {
    const group = entry.kind === "item" ? entry.item.group : undefined;
    const lastGroup = groups[groups.length - 1];
    if (!group) {
      ungrouped.push(renderEntry(entry, index));
    } else if (lastGroup?.label === group) {
      lastGroup.options.push(renderEntry(entry, index));
    } else {
      groups.push({ label: group, options: [renderEntry(entry, index)] });
    }
  });

  return (
    <>
      {groups.map(group => (
        <Combobox.Group key={`group-${group.label}`} label={group.label}>
          {group.options}
        </Combobox.Group>
      ))}
      {groups.length > 0 && ungrouped.length > 0 ? <Divider className={classes.separator} /> : null}
      {ungrouped}
    </>
  );
};

interface SelectDropdownProps extends OptionListProps {
  hidden: boolean;
  nothingFound?: React.ReactNode;
  maxDropdownHeight: number | string;
  dropdownComponent?: React.ElementType;
  direction: OptionsDirection;
  labelledBy?: string;
  ariaLabel?: string;
  optionsRef: React.RefObject<HTMLDivElement>;
}

/**
 * Mantine 6's dropdown: a `maxDropdownHeight` scroll area (or the caller's `dropdownComponent` in its place) and
 * `nothingFound` when no row matches.
 */
const SelectDropdown = ({
  hidden,
  nothingFound,
  maxDropdownHeight,
  dropdownComponent: DropdownComponent,
  direction,
  labelledBy,
  ariaLabel,
  optionsRef,
  ...listProps
}: SelectDropdownProps) => {
  const list = <OptionList {...listProps} />;
  const rows = direction === "column-reverse" ? <div style={REVERSED_OPTIONS}>{list}</div> : list;
  const maxHeight = typeof maxDropdownHeight === "number" ? rem(maxDropdownHeight) : maxDropdownHeight;

  return (
    <Combobox.Dropdown hidden={hidden}>
      <Combobox.Options aria-label={ariaLabel} labelledBy={labelledBy} ref={optionsRef}>
        {DropdownComponent ? (
          <DropdownComponent style={{ maxHeight, overflowY: "auto" }}>{rows}</DropdownComponent>
        ) : (
          <ScrollArea.Autosize mah={maxHeight} offsetScrollbars="y" scrollbarSize="var(--combobox-padding)" type="scroll">
            {rows}
          </ScrollArea.Autosize>
        )}
        {listProps.entries.length === 0 && nothingFound ? <Combobox.Empty>{nothingFound}</Combobox.Empty> : null}
      </Combobox.Options>
    </Combobox.Dropdown>
  );
};

/** Mantine 6 reversed the rows when `switchDirectionOnFlip` was set and the dropdown flipped above the input. */
const useOptionsDirection = (switchDirectionOnFlip: boolean) => {
  const [direction, setDirection] = React.useState<OptionsDirection>("column");
  const onPositionChange = switchDirectionOnFlip
    ? (position: string) => setDirection(position === "top" ? "column-reverse" : "column")
    : undefined;
  return { direction, onPositionChange };
};

const SelectBase = React.forwardRef<HTMLInputElement, SelectProps>(function Select(
  {
    data,
    value,
    defaultValue,
    onChange,
    filter = defaultSelectFilter,
    itemComponent,
    icon,
    iconWidth,
    rightSection,
    rightSectionProps,
    size,
    shadow = "sm",
    initiallyOpened = false,
    onDropdownOpen,
    onDropdownClose,
    withinPortal = false,
    limit = Infinity,
    nothingFound,
    zIndex,
    dropdownPosition = "flip",
    switchDirectionOnFlip = false,
    positionDependencies = [],
    itemClassName,
    inputClassName,
    rootClassName,
    dropdownComponent,
    clearButtonProps,
    maxDropdownHeight = 220,
    searchable = false,
    clearable = false,
    onSearchChange,
    onSearchQueryChange,
    searchValue,
    hoverOnSearchChange = false,
    creatable = false,
    getCreateLabel,
    shouldCreate = defaultSelectShouldCreate,
    onCreate,
    selectOnBlur = false,
    allowDeselect,
    filterDataOnExactSearchMatch = false,
    id,
    label,
    error,
    disabled,
    readOnly,
    name,
    form,
    autoComplete,
    onBlur,
    onClick,
    onFocus,
    onKeyDown,
    onMouseDown,
    ...rest
  },
  ref,
) {
  const inputId = useId(id);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const mergedRef = useMergedRef(ref, inputRef);
  const { direction, onPositionChange } = useOptionsDirection(switchDirectionOnFlip);
  // Mantine 6 opened on press, so a press that focuses a searchable Select opens it as a pointer open (above).
  const pressed = React.useRef(false);

  const items = groupItems(toItems(data));
  const [currentValue, setValue, controlled] = useUncontrolled<string | null>({ value, defaultValue, finalValue: null, onChange });
  const selectedItem = items.find(item => item.value === currentValue);
  const [search, setSearch] = useUncontrolled<string>({
    value: searchValue,
    defaultValue: selectedItem?.label ?? "",
    finalValue: "",
    onChange: onSearchChange,
  });

  const isCreatable = creatable && typeof getCreateLabel === "function";
  const filtered = filterSelectItems({ items, searchable, limit, search, filter, value: currentValue, filterDataOnExactSearchMatch });
  const createLabel = creatable && getCreateLabel && shouldCreate(search, filtered) ? getCreateLabel(search) : null;
  const entries = toEntries(filtered, createLabel ? search : null);
  const { combobox, optionsRef, hoverOption } = useSelectCombobox({
    initiallyOpened,
    entries,
    query: search,
    hoverOnSearchChange,
    valueRowOnly: !!currentValue,
    onDropdownOpen,
    onDropdownClose,
  });

  /** Mantine 6 let the current row deselect itself only when `allowDeselect` said so, defaulting to `clearable`. */
  const isDeselectable = allowDeselect ?? clearable;

  // Mantine 6 kept the input text on the selected label: on every value change (clearing it unless a created value
  // is still waiting for its item), and on a label change unless the user is typing into an open search.
  const syncedSelection = React.useRef<{ value: string | null; label: string | undefined } | null>(null);
  React.useEffect(() => {
    const previous = syncedSelection.current;
    syncedSelection.current = { value: currentValue, label: selectedItem?.label };
    if (!previous || previous.value !== currentValue) {
      if (selectedItem) {
        setSearch(selectedItem.label ?? "");
        onSearchQueryChange?.("");
      } else if (!isCreatable || !currentValue) {
        setSearch("");
        onSearchQueryChange?.("");
      }
    } else if (selectedItem && previous.label !== selectedItem.label && (!searchable || !combobox.dropdownOpened)) {
      setSearch(selectedItem.label ?? "");
      onSearchQueryChange?.("");
    }
  });

  const submitEntry = (entry: OptionEntry) => {
    if (readOnly) {
      return;
    }
    onSearchQueryChange?.("");
    if (entry.kind === "item" && isDeselectable && selectedItem?.value === entry.item.value) {
      setValue(null);
      combobox.closeDropdown();
      return;
    }

    // Mantine 6 emitted `onChange` for every pick, the current value included.
    if (entry.kind === "create") {
      const created = onCreate ? onCreate(entry.query) : entry.query;
      if (created !== null && created !== undefined) {
        setValue(typeof created === "string" ? created : created.value);
      }
    } else {
      setValue(entry.item.value);
    }
    if (!controlled) {
      setSearch(entry.kind === "create" ? entry.query : (entry.item.label ?? ""));
    }
    combobox.closeDropdown();
    inputRef.current?.focus();
  };

  const handleClear = () => {
    if (readOnly) {
      return;
    }
    onSearchQueryChange?.("");
    setValue(null);
    if (!controlled) {
      setSearch("");
    }
    inputRef.current?.focus();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) {
      return;
    }
    setSearch(event.currentTarget.value);
    onSearchQueryChange?.(event.currentTarget.value);
    if (clearable && event.currentTarget.value === "") {
      setValue(null);
    }
    combobox.openDropdown();
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLInputElement>) => {
    onMouseDown?.(event);
    pressed.current = true;
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(event);
    if (searchable) {
      combobox.openDropdown(pressed.current ? "mouse" : "unknown");
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    pressed.current = false;
    onBlur?.(event);
    onSearchQueryChange?.("");
    if (selectOnBlur && combobox.dropdownOpened && combobox.getSelectedOptionIndex() !== -1) {
      combobox.clickSelectedOption();
    } else {
      setSearch(selectedItem?.label ?? "");
    }
    combobox.closeDropdown();
  };

  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    pressed.current = false;
    onClick?.(event);
    if (readOnly) {
      return;
    }
    if (searchable) {
      combobox.openDropdown("mouse");
    } else {
      combobox.toggleDropdown("mouse");
    }
  };

  // Mantine 6's non-searchable Select kept Enter from submitting the form without opening on it, and let Space pick
  // the hovered row or open on the current value's row. The Combobox target stays an input target, so its own
  // button-target Enter open and Space toggle never run.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (searchable || (event.key !== " " && event.key !== "Enter")) {
      return;
    }
    event.preventDefault();
    if (event.key !== " " || readOnly) {
      return;
    }
    if (!combobox.dropdownOpened) {
      combobox.openDropdown("keyboard");
    } else if (combobox.getSelectedOptionIndex() === -1) {
      combobox.selectActiveOption();
    } else {
      combobox.clickSelectedOption();
    }
  };

  const shouldClear = clearable && !!selectedItem;
  const defaultRightSection = renderDefaultRightSection({
    readOnly,
    disabled,
    shouldClear,
    onClear: handleClear,
    clearButtonProps,
    error,
    size,
  });

  return (
    <>
      <Combobox
        __staticSelector="Select"
        keepMounted={false}
        middlewares={dropdownPosition === "flip" ? FLIP_MIDDLEWARES : FIXED_MIDDLEWARES}
        onOptionSubmit={optionValue => {
          const entry = entries[Number(optionValue)];
          if (entry) {
            submitEntry(entry);
          }
        }}
        onPositionChange={onPositionChange}
        position={dropdownPosition === "flip" ? "bottom" : dropdownPosition}
        positionDependencies={[...positionDependencies, search]}
        readOnly={readOnly}
        shadow={shadow}
        size={size}
        store={combobox}
        withinPortal={withinPortal}
        zIndex={zIndex}
      >
        <Combobox.Target autoComplete={autoComplete ?? "off"}>
          <InputBase
            type="search"
            {...rest}
            __staticSelector="Select"
            classNames={{ input: inputClassName, root: rootClassName }}
            data-sc-input="true"
            disabled={disabled}
            error={error}
            id={inputId}
            label={label}
            leftSection={icon}
            leftSectionWidth={iconWidth}
            onBlur={handleBlur}
            onChange={handleInputChange}
            onClick={handleClick}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            onMouseDown={handleMouseDown}
            pointer={!searchable}
            readOnly={readOnly || !searchable}
            ref={mergedRef}
            rightSection={rightSection || defaultRightSection}
            // Mantine 6 left a caller's section its pointer events (6.0.19 get-select-right-section-props.js:47-48);
            // Mantine 7's Input defaults every section to "none" (7.17.8 Input.mjs:33).
            rightSectionPointerEvents={rightSection ? "auto" : shouldClear ? "all" : "none"}
            rightSectionProps={toRightSectionProps(rightSectionProps, disabled)}
            size={size}
            value={search}
          />
        </Combobox.Target>
        <SelectDropdown
          ariaLabel={label ? undefined : rest["aria-label"]}
          createLabel={createLabel}
          direction={direction}
          dropdownComponent={dropdownComponent}
          entries={entries}
          hidden={!!readOnly || (entries.length === 0 && !nothingFound)}
          isSelected={optionValue => optionValue === currentValue}
          itemClassName={itemClassName}
          itemComponent={itemComponent}
          labelledBy={label ? `${inputId}-label` : undefined}
          maxDropdownHeight={maxDropdownHeight}
          nothingFound={nothingFound}
          onOptionHover={hoverOption}
          optionsRef={optionsRef}
        />
      </Combobox>
      <Combobox.HiddenInput disabled={disabled} form={form} name={name} value={currentValue} />
    </>
  );
});

const MultiSelectBase = React.forwardRef<HTMLInputElement, MultiSelectProps>(function MultiSelect(
  {
    data,
    value,
    defaultValue,
    onChange,
    filter = defaultMultiSelectFilter,
    itemComponent,
    valueComponent: ValueComponent,
    icon,
    iconWidth,
    rightSection,
    rightSectionWidth,
    rightSectionProps,
    radius,
    variant,
    size,
    shadow: _shadow,
    initiallyOpened = false,
    onDropdownOpen,
    onDropdownClose,
    withinPortal = false,
    limit = Infinity,
    nothingFound,
    zIndex,
    dropdownPosition = "flip",
    switchDirectionOnFlip = false,
    positionDependencies = [],
    itemClassName,
    inputClassName,
    rootClassName,
    dropdownComponent,
    clearButtonProps,
    maxDropdownHeight = 220,
    searchable = false,
    clearSearchOnChange = true,
    clearable = false,
    disableSelectedItemFiltering = false,
    clearSearchOnBlur = false,
    onSearchChange,
    searchValue,
    hoverOnSearchChange = false,
    creatable = false,
    getCreateLabel,
    shouldCreate = defaultMultiSelectShouldCreate,
    onCreate,
    maxSelectedValues,
    selectOnBlur = false,
    label,
    description,
    error,
    withAsterisk,
    labelProps,
    descriptionProps,
    errorProps,
    wrapperProps,
    inputContainer,
    inputWrapperOrder,
    id,
    className,
    style,
    required,
    disabled,
    readOnly,
    name,
    form,
    placeholder,
    autoComplete,
    type: _type,
    onBlur,
    onClick,
    onFocus,
    onKeyDown,
    ...others
  },
  ref,
) {
  const inputId = useId(id);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const mergedRef = useMergedRef(ref, inputRef);
  const { styleProps, rest } = extractStyleProps(others);
  const { direction, onPositionChange } = useOptionsDirection(switchDirectionOnFlip);

  const items = groupItems(toItems(data));
  const [currentValue, setValue] = useUncontrolled<string[]>({
    value: valuesInData(value, data),
    defaultValue: valuesInData(defaultValue, data),
    finalValue: [],
    onChange,
  });
  const [search, setSearch] = useUncontrolled<string>({ value: searchValue, defaultValue: "", finalValue: "", onChange: onSearchChange });

  /** Mantine 6 stopped taking picks, and closed and locked the dropdown, once `maxSelectedValues` were chosen. */
  const valuesOverflow = maxSelectedValues !== undefined && maxSelectedValues > 0 && currentValue.length >= maxSelectedValues;
  const isCreatable = creatable && typeof getCreateLabel === "function";
  const filtered = filterMultiSelectItems({ items, searchable, limit, search, filter, value: currentValue, disableSelectedItemFiltering });
  const createLabel = creatable && getCreateLabel && shouldCreate(search, items) ? getCreateLabel(search) : null;
  const entries = toEntries(filtered, createLabel ? search : null);
  const { combobox, optionsRef, hoverOption } = useSelectCombobox({
    initiallyOpened,
    entries,
    query: search,
    hoverOnSearchChange,
    clampSelection: true,
    onDropdownOpen,
    onDropdownClose,
  });

  // Mantine 6 closed the dropdown when a value change reached the limit or outgrew the data.
  const valueKey = JSON.stringify(currentValue);
  useDidUpdate(() => {
    if ((!disabled && currentValue.length > data.length) || valuesOverflow) {
      combobox.closeDropdown();
    }
  }, [valueKey]);

  const removeValue = (removed: string) => {
    if (!readOnly) {
      setValue(currentValue.filter(selectedValue => selectedValue !== removed));
    }
  };

  const submitEntry = (entry: OptionEntry) => {
    if (readOnly) {
      return;
    }
    if (clearSearchOnChange) {
      setSearch("");
    }

    const picked = entry.kind === "item" ? entry.item.value : entry.query;
    if (currentValue.includes(picked)) {
      removeValue(picked);
      return;
    }

    if (entry.kind === "create") {
      const created = onCreate ? onCreate(entry.query) : entry.query;
      if (created !== null && created !== undefined) {
        setValue([...currentValue, typeof created === "string" ? created : created.value]);
      }
    } else {
      setValue([...currentValue, entry.item.value]);
    }
    if (currentValue.length === (maxSelectedValues ?? 0) - 1 || entries.length === 1) {
      combobox.closeDropdown();
    }
  };

  const handleClear = () => {
    setSearch("");
    setValue([]);
    inputRef.current?.focus();
  };

  const canOpen = !disabled && !valuesOverflow;

  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
    if (canOpen && searchable) {
      combobox.openDropdown();
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(event);
    if (canOpen && searchable) {
      combobox.openDropdown();
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(event);
    if (selectOnBlur && combobox.dropdownOpened && combobox.getSelectedOptionIndex() !== -1) {
      combobox.clickSelectedOption();
    }
    if (clearSearchOnBlur) {
      setSearch("");
    }
    combobox.closeDropdown();
  };

  // PillsInput.Field keeps a searchable field's press from reaching the PillsInput toggle below. Mantine 6 toggled on
  // every press; the first press's focus already opens the list, so a click on the focused field only reopens it.
  const handleFieldClick = (event: React.MouseEvent<HTMLInputElement>) => {
    onClick?.(event);
    if (canOpen && searchable) {
      combobox.openDropdown();
    }
  };

  // Arrow, Escape and hovered-row Enter handling is the Combobox target's; these are the keys Mantine 6 added on top.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) {
      return;
    }
    onKeyDown?.(event);
    if (readOnly) {
      return;
    }
    // Mantine 6 never let Enter submit the enclosing form (it ignored every key but Backspace once full), and opened
    // a closed list on it without hovering a row.
    if (event.key === "Enter" && !valuesOverflow) {
      event.preventDefault();
      if (!combobox.dropdownOpened && canOpen) {
        combobox.openDropdown();
      }
    }
    // Space on a non-searchable MultiSelect picked the hovered row, else opened the list, again without hovering one.
    if (event.key === " " && !searchable) {
      event.preventDefault();
      if (combobox.dropdownOpened && combobox.getSelectedOptionIndex() !== -1) {
        combobox.clickSelectedOption();
      } else if (canOpen) {
        combobox.openDropdown();
      }
    }
    if (event.key === "Backspace" && currentValue.length > 0 && search.length === 0) {
      setValue(currentValue.slice(0, -1));
      combobox.openDropdown();
    }
  };

  // Mantine 6 showed a selected value only while its row exists and is enabled, or as the raw value when creatable.
  const selectedItems = currentValue.flatMap((selectedValue): SelectItem[] => {
    const item = items.find(candidate => candidate.value === selectedValue && !candidate.disabled);
    if (item) {
      return [item];
    }
    return isCreatable ? [{ value: selectedValue, label: selectedValue }] : [];
  });

  const values = selectedItems.map(item => {
    if (!ValueComponent) {
      return (
        <Pill
          disabled={disabled}
          key={item.value}
          onRemove={() => removeValue(item.value)}
          radius={radius ?? "sm"}
          withRemoveButton={!disabled && !readOnly}
        >
          {item.label}
        </Pill>
      );
    }

    // Mantine 6 called `onRemove` as an event handler and kept the press from reaching the input.
    const handleRemove = (event?: React.SyntheticEvent) => {
      event?.preventDefault();
      event?.stopPropagation();
      removeValue(item.value);
    };
    return (
      <ValueComponent
        {...item}
        disabled={!!disabled}
        key={item.value}
        label={item.label ?? item.value}
        onRemove={handleRemove}
        readOnly={!!readOnly}
      />
    );
  });

  const shouldClear = clearable && currentValue.length > 0;
  const defaultRightSection = renderDefaultRightSection({
    readOnly,
    disabled,
    shouldClear,
    onClear: handleClear,
    clearButtonProps,
    error,
    size,
  });

  return (
    <>
      <Combobox
        __staticSelector="MultiSelect"
        keepMounted={false}
        middlewares={dropdownPosition === "flip" ? FLIP_MIDDLEWARES : FIXED_MIDDLEWARES}
        onOptionSubmit={optionValue => {
          const entry = entries[Number(optionValue)];
          if (entry) {
            submitEntry(entry);
          }
        }}
        onPositionChange={onPositionChange}
        position={dropdownPosition === "flip" ? "bottom" : dropdownPosition}
        positionDependencies={[...positionDependencies, search]}
        readOnly={readOnly}
        // Mantine 6's MultiSelect always drew its dropdown with the `sm` shadow; its `shadow` prop was never read.
        shadow="sm"
        size={size}
        store={combobox}
        withinPortal={withinPortal}
        zIndex={zIndex}
      >
        <Combobox.DropdownTarget>
          <PillsInput
            {...styleProps}
            __staticSelector="MultiSelect"
            className={className}
            classNames={{ input: inputClassName, root: rootClassName }}
            description={description}
            descriptionProps={descriptionProps}
            disabled={disabled}
            error={error}
            errorProps={errorProps}
            id={inputId}
            inputContainer={inputContainer}
            inputWrapperOrder={inputWrapperOrder}
            label={label}
            labelProps={labelProps}
            leftSection={icon}
            leftSectionWidth={iconWidth}
            onMouseDown={() => {
              // Mantine 6 toggled on press; the PillsInput then focuses the search field.
              if (canOpen) {
                combobox.toggleDropdown();
              }
            }}
            pointer={!searchable}
            radius={radius}
            required={required}
            rightSection={rightSection || defaultRightSection}
            // Mantine 6 left a caller's section its pointer events (6.0.19 get-select-right-section-props.js:47-48);
            // Mantine 7's Input defaults every section to "none" (7.17.8 Input.mjs:33).
            rightSectionPointerEvents={rightSection ? "auto" : shouldClear ? "all" : "none"}
            rightSectionProps={toRightSectionProps(rightSectionProps, disabled)}
            rightSectionWidth={rightSectionWidth}
            size={size}
            style={style}
            variant={variant}
            withAsterisk={withAsterisk}
            wrapperProps={wrapperProps}
          >
            <Pill.Group disabled={disabled}>
              {values}
              <Combobox.EventsTarget autoComplete={autoComplete ?? "off"} withKeyboardNavigation={!valuesOverflow}>
                <PillsInput.Field
                  {...rest}
                  disabled={disabled}
                  id={inputId}
                  onBlur={handleBlur}
                  onChange={handleSearchInput}
                  onClick={handleFieldClick}
                  onFocus={handleFocus}
                  onKeyDown={handleKeyDown}
                  placeholder={currentValue.length === 0 ? placeholder : undefined}
                  pointer={!searchable}
                  readOnly={!searchable || valuesOverflow || readOnly}
                  ref={mergedRef}
                  type={currentValue.length > 0 && (!searchable || !combobox.dropdownOpened) ? "hidden" : "visible"}
                  value={search}
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </PillsInput>
        </Combobox.DropdownTarget>
        <SelectDropdown
          ariaLabel={label ? undefined : rest["aria-label"]}
          createLabel={createLabel}
          direction={direction}
          dropdownComponent={dropdownComponent}
          entries={entries}
          hidden={!!readOnly || (entries.length === 0 && !nothingFound)}
          isSelected={optionValue => currentValue.includes(optionValue)}
          itemClassName={itemClassName}
          itemComponent={itemComponent}
          labelledBy={label ? `${inputId}-label` : undefined}
          maxDropdownHeight={maxDropdownHeight}
          nothingFound={nothingFound}
          onOptionHover={hoverOption}
          optionsRef={optionsRef}
        />
      </Combobox>
      <Combobox.HiddenInput disabled={disabled} form={form} name={name} value={currentValue} />
    </>
  );
});

type SelectComponent = <Item extends SelectItem = SelectItem>(
  props: SelectProps<Item> & React.RefAttributes<HTMLInputElement>,
) => React.ReactElement | null;

type MultiSelectComponent = <Item extends SelectItem = SelectItem>(
  props: MultiSelectProps<Item> & React.RefAttributes<HTMLInputElement>,
) => React.ReactElement | null;

/**
 * React's `forwardRef` loses a function component's item generic. These casts restore only that
 * generic call signature; both bases already forward the exact same props/ref at runtime. The
 * casts test assigns custom item metadata and a real input ref to each public generic prop contract.
 */
export const Select = SelectBase as SelectComponent;
export const MultiSelect = MultiSelectBase as MultiSelectComponent;

/**
 * Free-text select surface. Unlike Select, its value need not correspond to an entry in `data`.
 * Metadata remains owned by the concrete item type supplied by each caller.
 */
export interface AutocompleteProps<Item extends SelectItem = SelectItem>
  extends Omit<ElementProps<"input">, "defaultValue" | "onChange" | "size" | "value">, StyleProps, FieldBaseProps {
  data: ReadonlyArray<string | Item>;
  value?: string;
  defaultValue?: string;
  onChange?(value: string): void;
  filter?: (query: string, item: Item) => boolean;
  itemComponent?: SelectRenderer<SelectItemComponentProps<Item>>;
  /** Receives the complete selected item, including consumer-owned metadata. */
  onItemSubmit?(item: Item): void;
  icon?: React.ReactNode;
  iconWidth?: React.CSSProperties["width"];
  rightSection?: React.ReactNode;
  rightSectionWidth?: React.CSSProperties["width"];
  rightSectionProps?: Record<string, unknown>;
  radius?: SizeValue;
  variant?: "default" | "filled" | "unstyled";
  size?: Exclude<SizeValue, number>;
  shadow?: string;
  initiallyOpened?: boolean;
  onDropdownOpen?: () => void;
  onDropdownClose?: () => void;
  withinPortal?: boolean;
  limit?: number;
  nothingFound?: React.ReactNode;
  zIndex?: React.CSSProperties["zIndex"];
  dropdownPosition?: "bottom" | "top" | "flip";
  switchDirectionOnFlip?: boolean;
  positionDependencies?: unknown[];
  dropdownComponent?: React.ElementType;
  maxDropdownHeight?: number | string;
  hoverOnSearchChange?: boolean;
  itemClassName?: string;
  inputClassName?: string;
  rootClassName?: string;
}

/** Mantine 6 Autocomplete: string data lifts to `{ value }`, filtering stops at `limit`, then rows group. */
const filterAutocompleteItems = (
  data: ReadonlyArray<string | SelectItem>,
  query: string,
  limit: number,
  filter: (query: string, item: SelectItem) => boolean,
): SelectItem[] =>
  groupItems(
    takeMatches(
      data.map(item => (typeof item === "string" ? { value: item } : item)),
      limit,
      item => filter(query, item),
    ),
  );

const AutocompleteBase = React.forwardRef<HTMLInputElement, AutocompleteProps>(function Autocomplete(
  {
    data,
    value,
    defaultValue,
    onChange,
    filter = defaultAutocompleteFilter,
    itemComponent,
    onItemSubmit,
    icon,
    iconWidth,
    rightSectionProps,
    size,
    shadow: _shadow,
    initiallyOpened = false,
    onDropdownOpen,
    onDropdownClose,
    withinPortal = false,
    limit = 5,
    nothingFound,
    zIndex,
    dropdownPosition = "flip",
    switchDirectionOnFlip = false,
    positionDependencies = [],
    dropdownComponent,
    maxDropdownHeight = "auto",
    hoverOnSearchChange = false,
    itemClassName,
    inputClassName,
    rootClassName,
    id,
    label,
    readOnly,
    autoComplete,
    onBlur,
    onClick,
    onFocus,
    ...rest
  },
  ref,
) {
  const inputId = useId(id);
  const { direction, onPositionChange } = useOptionsDirection(switchDirectionOnFlip);
  const [currentValue, setValue] = useUncontrolled<string>({ value, defaultValue, finalValue: "", onChange });
  const entries = toEntries(filterAutocompleteItems(data, currentValue, limit, filter), null);
  const { combobox, optionsRef, hoverOption } = useSelectCombobox({
    initiallyOpened,
    entries,
    query: currentValue,
    hoverOnSearchChange,
    onDropdownOpen,
    onDropdownClose,
  });

  const submitEntry = (entry: OptionEntry) => {
    if (entry.kind === "item") {
      setValue(entry.item.value);
      onItemSubmit?.(entry.item);
    }
    combobox.closeDropdown();
  };

  return (
    <Combobox
      __staticSelector="Autocomplete"
      keepMounted={false}
      middlewares={dropdownPosition === "flip" ? FLIP_MIDDLEWARES : FIXED_MIDDLEWARES}
      onOptionSubmit={optionValue => {
        const entry = entries[Number(optionValue)];
        if (entry) {
          submitEntry(entry);
        }
      }}
      onPositionChange={onPositionChange}
      position={dropdownPosition === "flip" ? "bottom" : dropdownPosition}
      positionDependencies={positionDependencies}
      readOnly={readOnly}
      // Mantine 6's Autocomplete always drew its dropdown with the `sm` shadow; its `shadow` prop was never read.
      shadow="sm"
      size={size}
      store={combobox}
      withinPortal={withinPortal}
      zIndex={zIndex}
    >
      <Combobox.Target autoComplete={autoComplete ?? "off"}>
        <InputBase
          type="search"
          {...rest}
          __staticSelector="Autocomplete"
          classNames={{ input: inputClassName, root: rootClassName }}
          data-sc-input="true"
          id={inputId}
          label={label}
          leftSection={icon}
          leftSectionWidth={iconWidth}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            onBlur?.(event);
            combobox.closeDropdown();
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setValue(event.currentTarget.value);
            combobox.openDropdown();
          }}
          onClick={(event: React.MouseEvent<HTMLInputElement>) => {
            onClick?.(event);
            combobox.openDropdown();
          }}
          onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
            onFocus?.(event);
            combobox.openDropdown();
          }}
          readOnly={readOnly}
          ref={ref}
          // Mantine 6's Autocomplete left a caller's section its pointer events; Mantine 7's Input defaults it to "none".
          rightSectionPointerEvents={rest.rightSection ? "auto" : undefined}
          rightSectionProps={toRightSectionProps(rightSectionProps, rest.disabled)}
          size={size}
          value={currentValue}
        />
      </Combobox.Target>
      <SelectDropdown
        ariaLabel={label ? undefined : rest["aria-label"]}
        createLabel={null}
        direction={direction}
        dropdownComponent={dropdownComponent}
        entries={entries}
        hidden={!!readOnly || (entries.length === 0 && !nothingFound)}
        isSelected={() => false}
        itemClassName={itemClassName}
        itemComponent={itemComponent}
        labelledBy={label ? `${inputId}-label` : undefined}
        maxDropdownHeight={maxDropdownHeight}
        nothingFound={nothingFound}
        onOptionHover={hoverOption}
        optionsRef={optionsRef}
      />
    </Combobox>
  );
});

type AutocompleteComponent = <Item extends SelectItem = SelectItem>(
  props: AutocompleteProps<Item> & React.RefAttributes<HTMLInputElement>,
) => React.ReactElement | null;

/** See Select's generic restoration above; the runtime component is unchanged. */
export const Autocomplete = AutocompleteBase as AutocompleteComponent;
